import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { Toolbar } from "@examspark/common-ui";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import { scoreCartesianInequality } from "@examspark/score";
import { Stem, StemCartesianInequality } from "@examspark/types";

import Mark from "../common/Mark";
import QuestionContext from "../QuestionContext";

import CartesianInequalityComponent from "./common/graph/cartesian/CartesianInequality";
import { ShapeControlButtons } from "./common/graph/cartesian/CartesianInequalityControls";
import Prompt from "./shared/Prompt";

const Wrapper = styled.div`
  max-width: 55rem;
`;

interface MarkProps {
  mark?: "correct" | "incorrect";
}

const Styled = styled.div<MarkProps>`
  /* display: flex; */
  position: relative;
  padding: 1rem 1rem;
  border-top: solid 3px transparent;
  background-color: #f7f7f7;

  ${(props) => {
    if (props.mark === "correct") {
      return `
      background-color: #e4f3e4;
      border-top: solid 3px #9cb39c;
      `;
    }
    if (props.mark === "incorrect") {
      return `
      background-color: #f3e2e2;
      border-top: solid 3px #b32e2b;
      `;
    }
  }}

  .markup {
    position: absolute;
    top: -11px;
    right: -8px;
  }
`;

const GraphToolbar = styled(Toolbar)`
  background-color: ${colorFromTheme("surface5")};
  padding: 0.5rem;
`;

export interface ItemCartesianGraphProps {
  data: StemCartesianInequality;
}

const CartesianInequality: React.FC<
  React.PropsWithChildren<ItemCartesianGraphProps>
> = (props) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);
  const [mode, setMode] = useState<string>();

  const actualResponse = player.response?.[props.data.id] as
    | Partial<Stem.Response>
    | undefined;

  // Reveal Key
  useEffect(() => {
    if (!player.response) return;
    if (player.revealKey) {
      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            shapes: props.data.scoring?.shapes,
            inequality: props.data.scoring?.inequality,
          },
        },
      });
    }
  }, [player.revealKey]);

  // deselect the line tool automatically after hitting the max # of allowed lines
  useEffect(() => {
    if (
      (actualResponse?.shapes?.length ?? 0) >= 2 &&
      (mode === "line" || mode === "dashed-line")
    ) {
      setMode(undefined);
    }
  }, [mode, player]);

  // reset the quadrant if they reduce the # of lines
  const lastUsedShapeCount = useRef(actualResponse?.shapes?.length);
  useEffect(() => {
    const previousShapeCount = lastUsedShapeCount.current || 0;
    const currentShapeCount = actualResponse?.shapes?.length || 0;
    if (previousShapeCount > currentShapeCount) {
      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          response: {
            shapes: actualResponse?.shapes,
            inequality: undefined,
          },
        },
      });
    }
    lastUsedShapeCount.current = actualResponse?.shapes?.length;
  }, [actualResponse?.shapes?.length]);

  let mark: "incorrect" | "correct" | undefined;
  if (player.scoreResponses) {
    mark = "incorrect";

    if (actualResponse) {
      const score = scoreCartesianInequality(props.data, actualResponse);
      // console.log("score cartesian", score);
      if (score.max === score.actual) {
        mark = "correct";
      }
    }
  }

  return (
    <>
      <Prompt data={props.data.content.prompt} />
      <Wrapper>
        <GraphToolbar>
          <ShapeControlButtons
            mode={mode}
            setMode={setMode}
            shapes={actualResponse?.shapes || []}
          />
        </GraphToolbar>
        <Styled mark={mark}>
          {mark && <Mark type={mark} />}
          <CartesianInequalityComponent
            {...props.data.config.graph}
            shapes={actualResponse?.shapes || []}
            mode={mode}
            selectedQuadrant={actualResponse?.inequality?.quadrant}
            onChange={(scoring) => {
              setMode(undefined);
              dispatchPlayer({
                type: "RESPONSE",
                data: {
                  id: props.data.id,
                  response: {
                    shapes: scoring.shapes ?? actualResponse?.shapes,
                    inequality:
                      scoring.inequality ?? actualResponse?.inequality,
                  },
                },
              });
            }}
          />
        </Styled>
      </Wrapper>
    </>
  );
};

export default CartesianInequality;
